import { z } from 'zod';

import { apiRequest } from '@/api/client';
import { focalPointSchema } from '@/features/storefront/sections/schema';
import { resolveStorefrontHost } from '@/features/storefront/storefrontHost';
import { storefrontLanguageHeaders } from '@/features/storefront/storefrontLanguageHeader';

/**
 * The people a service business books against, for the `team` section, the
 * `/team/:slug` profile page and the bookings section's person picker.
 *
 * Mirrors `TeamMemberPublic` in `backend/app/schemas/team_member.py`. The
 * people are rows the merchant keeps on the Team screen - never settings on a
 * section - so a doctor who leaves the clinic leaves every page at once.
 *
 * `hours` is the bookings section's own shape (weekday 0 = Monday, wall-clock
 * `HH:MM` in the store's timezone); `null` means "the section's hours". An
 * empty `service_ids` means the person offers every service, which is what a
 * one-chair salon means without having to tick every box.
 */
const hoursSchema = z.object({
  weekday: z.number(),
  closed: z.boolean().default(false),
  opens: z.string().default('09:00'),
  closes: z.string().default('17:00'),
});

/** The same focal point every other picture carries; tolerated when malformed. */
const focalSchema = focalPointSchema
  .nullish()
  .catch(null)
  .transform((value) => value ?? null);

const text = z
  .string()
  .nullish()
  .transform((value) => value ?? null);

/**
 * The profile facts the search filters and the tabbed profile read. Every one
 * is optional on the server and tolerated here when missing or malformed, so a
 * shop served by an older API (or a hand-written sample) parses to "not
 * stated" - an empty list or null - and the filter or block that would have
 * read it simply does not appear.
 */
export const TEAM_GENDERS = ['female', 'male', 'other'] as const;
export type TeamGender = (typeof TEAM_GENDERS)[number];

export const VISIT_FORMATS = ['in_person', 'online', 'home'] as const;
export type VisitFormat = (typeof VISIT_FORMATS)[number];

/** A list the server may not send yet: anything but an array of `item` reads as empty. */
function listOf<T extends z.ZodTypeAny>(item: T) {
  return z
    .array(item)
    .nullish()
    .catch(null)
    .transform((value): z.output<T>[] => value ?? []);
}

/** One line of the education timeline. */
const educationSchema = z.object({ year: text, title: z.string(), place: text });

/** One line of the career timeline. */
const careerSchema = z.object({ period: text, title: z.string(), place: text });

export const storefrontTeamMemberSchema = z.object({
  id: z.string(),
  slug: z.string(),
  name: z.string(),
  photo_url: text,
  photo_focal: focalSchema,
  role: text,
  speciality: text,
  qualifications: text,
  short_bio: text,
  bio: text,
  service_ids: z.array(z.string()).nullish().transform((value) => value ?? []),
  hours: z
    .array(hoursSchema)
    .nullish()
    .transform((value) => value ?? null),
  fee_note: text,
  gender: z
    .enum(TEAM_GENDERS)
    .nullish()
    .catch(null)
    .transform((value) => value ?? null),
  experience_years: z
    .number()
    .int()
    .min(0)
    .nullish()
    .catch(null)
    .transform((value) => value ?? null),
  // An unknown format is dropped on its own rather than losing the list.
  visit_formats: listOf(z.string()).transform((values) =>
    values.filter((value): value is VisitFormat =>
      (VISIT_FORMATS as readonly string[]).includes(value),
    ),
  ),
  languages: listOf(z.string()),
  education: listOf(educationSchema),
  career: listOf(careerSchema),
  /**
   * The weekdays (0 = Monday) the person works, worked out by the server from
   * their own hours or the bookings section that can book them; empty when
   * nobody knows. Read by the team section's weekday filter.
   */
  available_days: listOf(z.number().int().min(0).max(6)),
  /**
   * Seeded with the store's theme and not yet adopted. A sample person with
   * no photo is drawn with the preview's silhouette (`memberPhoto`), where a
   * merchant's own shows their initials.
   */
  is_sample: z.boolean().optional(),
  /** The first day in the next fortnight with a free slot (`YYYY-MM-DD`), best effort. */
  next_available_date: text,
});

/**
 * The visible people, in the merchant's order, with what this shop calls one
 * of them ("Doctor", "Stylist") - the per-industry word, or the merchant's own.
 */
export const storefrontTeamSchema = z.object({
  singular: z.string().default('Team member'),
  plural: z.string().default('Team'),
  members: z.array(storefrontTeamMemberSchema).default([]),
});

export type StorefrontTeamMember = z.infer<typeof storefrontTeamMemberSchema>;
export type StorefrontTeam = z.infer<typeof storefrontTeamSchema>;
export type TeamMemberHours = z.infer<typeof hoursSchema>;
export type TeamMemberEducation = z.infer<typeof educationSchema>;
export type TeamMemberCareer = z.infer<typeof careerSchema>;

/**
 * "Nothing stated" for every profile fact, for the people drawn without a
 * server - stand-ins, samples, tests - that only want to say who someone is.
 */
export const NO_PROFILE_FACTS: Pick<
  StorefrontTeamMember,
  | 'gender'
  | 'experience_years'
  | 'visit_formats'
  | 'languages'
  | 'education'
  | 'career'
  | 'available_days'
  | 'next_available_date'
> = {
  gender: null,
  experience_years: null,
  visit_formats: [],
  languages: [],
  education: [],
  career: [],
  available_days: [],
  next_available_date: null,
};

export const NO_TEAM: StorefrontTeam = { singular: 'Team member', plural: 'Team', members: [] };

function publicHeaders(host?: string | null): Record<string, string> {
  const resolved = host ?? resolveStorefrontHost();
  return {
    ...(resolved ? { 'X-Storefront-Host': resolved } : {}),
    ...storefrontLanguageHeaders(),
  };
}

/**
 * `GET /storefront/team-members`. Public, like the rest of the storefront: no
 * merchant token travels with it, and the shop is named in a header.
 */
export function fetchStorefrontTeam(host?: string | null): Promise<StorefrontTeam> {
  return apiRequest('/storefront/team-members', storefrontTeamSchema, {
    cache: 'no-store',
    auth: 'none',
    headers: publicHeaders(host),
  });
}

/** `GET /storefront/team-members/{slug}` - 404 for a hidden or unknown person. */
export function fetchStorefrontTeamMember(
  slug: string,
  host?: string | null,
): Promise<StorefrontTeamMember> {
  return apiRequest(
    `/storefront/team-members/${encodeURIComponent(slug)}`,
    storefrontTeamMemberSchema,
    {
      cache: 'no-store',
      auth: 'none',
      headers: publicHeaders(host),
    },
  );
}
